import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../Button';
import { PasswordReveal } from './PasswordReveal';
import { RadioGroup } from './RadioGroup';
import { TextField } from './TextField';
import { generatePassword } from '../../lib/generatePassword';
import { PASSWORD_HELP, passwordError } from '../../lib/passwordRules';

/** How the password is set. Either way the value is on screen before anything is submitted. */
export type PasswordMode = 'auto' | 'manual';

export interface PasswordFieldsState {
  /** The password to submit. Empty until the manual pair is valid. */
  password: string;
  valid: boolean;
  mode: PasswordMode;
}

/**
 * The shared "choose a password" block — the mode picker, the generated value, and the
 * manual pair.
 *
 * Used wherever a password is set: creating an account and resetting one. It exists as a
 * component because these are the same rules, the same reveal affordance, and the same match
 * check every time; two copies would be two places for the complexity rules to drift apart.
 *
 * Autogenerate shows the value rather than hiding it until after the save, so whoever is
 * setting it can copy it and hand it over there and then. It is masked by default and
 * revealed deliberately.
 */
export function PasswordFields({
  idPrefix,
  disabled = false,
  serverError,
  onChange,
  resetKey,
}: {
  /** Prefix for every generated DOM id. */
  idPrefix: string;
  disabled?: boolean;
  /** A field error the server returned for `password`. */
  serverError?: string;
  /** Fires whenever the resulting password or its validity changes. Must be stable. */
  onChange: (state: PasswordFieldsState) => void;
  /** Change it to clear the fields and mint a fresh password — e.g. when a modal opens. */
  resetKey?: unknown;
}) {
  const [mode, setMode] = useState<PasswordMode>('auto');
  const [generated, setGenerated] = useState(() => generatePassword());
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [visible, setVisible] = useState(false);

  // A reset must not leave the previous attempt's value behind, and the next account must
  // not be offered the password the last one was given.
  useEffect(() => {
    setMode('auto');
    setGenerated(generatePassword());
    setPassword('');
    setConfirmPassword('');
    setVisible(false);
  }, [resetKey]);

  const manual = mode === 'manual';
  const effective = manual ? password : generated;
  const valid = manual
    ? passwordError(password) === undefined && confirmPassword === password
    : generated.length > 0;

  useEffect(() => {
    onChange({ password: effective, valid, mode });
  }, [onChange, effective, valid, mode]);

  // Only surface a rule failure once something has been typed; an untouched field is not
  // yet wrong. The submit stays gated either way.
  const ruleError = manual && password.length > 0 ? passwordError(password) : undefined;
  const matchError =
    manual && confirmPassword.length > 0 && confirmPassword !== password
      ? 'The two passwords do not match.'
      : undefined;

  // The affordance is shared — see PasswordReveal. Two copies of it is how the two surfaces
  // end up with different icons or a different aria-label for the same control.
  const reveal = (id: string) => (
    <PasswordReveal id={id} visible={visible} onToggle={() => setVisible((v) => !v)} />
  );

  return (
    <div className="flex flex-col gap-4">
      <RadioGroup
        id={`${idPrefix}-password-mode`}
        label="Password"
        value={mode}
        onChange={setMode}
        // An option CARD carrying a description takes the full row — product call, 27 Oct 2026.
        // These two predate it and were still side by side, in every modal that sets a password.
        columns={1}
        disabled={disabled}
        options={[
          {
            value: 'auto',
            label: 'Autogenerate',
            description: 'A compliant password is generated for you, shown below.',
          },
          {
            value: 'manual',
            label: 'Set manually',
            description: 'Choose the password yourself and hand it over.',
          },
        ]}
      />

      {manual ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            id={`${idPrefix}-password`}
            label="Password"
            type={visible ? 'text' : 'password'}
            required
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={ruleError ?? serverError}
            description={PASSWORD_HELP}
            disabled={disabled}
            trailing={reveal(`${idPrefix}-password-reveal`)}
          />

          <TextField
            id={`${idPrefix}-confirm-password`}
            label="Confirm password"
            type={visible ? 'text' : 'password'}
            required
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={matchError}
            description="Must match the password above."
            disabled={disabled}
          />
        </div>
      ) : (
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <TextField
              id={`${idPrefix}-generated-password`}
              label="Generated password"
              type={visible ? 'text' : 'password'}
              readOnly
              value={generated}
              description={`${PASSWORD_HELP} Shown so you can hand it over; it is not stored anywhere.`}
              disabled={disabled}
              trailing={reveal(`${idPrefix}-generated-password-reveal`)}
            />
          </div>

          <Button
            id={`${idPrefix}-regenerate-password`}
            variant="secondary"
            leading={<RefreshCw className="h-4 w-4" />}
            onClick={() => setGenerated(generatePassword())}
            disabled={disabled}
          >
            Regenerate
          </Button>
        </div>
      )}
    </div>
  );
}

/** Stable initial state, so a parent can seed its own `useState` without repeating it. */
export function emptyPasswordState(): PasswordFieldsState {
  return { password: '', valid: false, mode: 'auto' };
}

/** Helper for parents that only need the callback identity to stay stable. */
export function usePasswordFieldsState() {
  const [state, setState] = useState<PasswordFieldsState>(emptyPasswordState);
  const onChange = useCallback((next: PasswordFieldsState) => setState(next), []);

  return { state, onChange };
}
